import React, { useState, useEffect, useContext } from 'react';
import {
    Container,
    Card,
    CardActionArea,
    CardContent,
    Typography,
    Box,
    Pagination,
    IconButton,
} from '@mui/material';
import { deleteDraft, listRecords, RecordListItem } from '../api/records';
import { useNavigate } from 'react-router-dom';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import { Tooltip } from '@mui/material';
import { AuthContext } from '../context/AuthContext';
// import RecordCard from '../components/RecordCard';

const PAGE_SIZE = 10;

const HomePage: React.FC = () => {
    const navigate = useNavigate();

    // pagination
    const [page, setPage] = useState(1);

    // available users for filtering
    // const [users, setUsers] = useState<AppUser[]>([]);

    // data
    const [total, setTotal] = useState(0);
    const [items, setItems] = useState<RecordListItem[]>([]);
    const [loading, setLoading] = useState(false);

    const { email } = useContext(AuthContext);

    // fetch data
    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const data = await listRecords({
                    page,
                    per_page: PAGE_SIZE,
                    drafts: true,
                    creators: [email || ''],
                });
                setTotal(data.total);
                setItems(data.items);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        })();
    }, [page, email]);

    const onCardClick = (id: string) => {
        navigate(`/record-editor/${id}`, {
            // state: { returnPath: location.pathname + location.search },
        });
    };

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Typography variant="h2" align="center" sx={{ mb: 2 }}>
                Drafts
            </Typography>
            {/* Card container */}
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0,
                    justifyContent: 'flex-start',
                }}
            >
                {items.map((rec, index) => (
                    // <RecordCard record={record} key={index}/>
                    <Card
                        key={rec.id}
                        sx={{
                            width: '100%',
                            boxShadow: 0,
                            borderRadius: 0,
                            bgcolor: index % 2 === 0 ? 'grey.400' : 'white', // alternating colors
                        }}
                    >
                        <CardActionArea
                            onClick={() => onCardClick(rec.triplet_id)}
                        >
                            <CardContent
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    px: 2, // horizontal padding
                                }}
                            >
                                {/* Box for record content in card. */}
                                <Box
                                    sx={{
                                        minWidth: '100%',
                                        maxWidth: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: {
                                                xs: 'column',
                                                sm: 'row',
                                            },
                                            flex: '0 0 100%',
                                            maxWidth: '100%',
                                        }}
                                    >
                                        {/* Left 95% content */}
                                        <Box
                                            sx={{
                                                maxWidth: '95%',
                                                flex: '0 0 95%',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: {
                                                        xs: 'column',
                                                        sm: 'row',
                                                    },
                                                    justifyContent:
                                                        'space-between',
                                                    alignItems: {
                                                        sm: 'center',
                                                    },
                                                    gap: 2, // gap between fields
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        display: 'block',
                                                        mb: 1,
                                                    }}
                                                >
                                                    record_time,
                                                    {(() => {
                                                        const isoUtc =
                                                            rec.record_time.endsWith(
                                                                'Z'
                                                            )
                                                                ? rec.record_time
                                                                : rec.record_time +
                                                                  'Z';

                                                        return new Date(
                                                            isoUtc
                                                        ).toLocaleString(
                                                            undefined,
                                                            {
                                                                year: 'numeric',
                                                                month: 'short',
                                                                day: 'numeric',
                                                                hour: '2-digit',
                                                                minute: '2-digit',
                                                                second: '2-digit',
                                                                hour12: false,
                                                            }
                                                        );
                                                    })()}
                                                    &nbsp;&nbsp;&nbsp;logged_time
                                                    ,&nbsp;
                                                    {(() => {
                                                        const isoUtc =
                                                            rec.created_at.endsWith(
                                                                'Z'
                                                            )
                                                                ? rec.created_at
                                                                : rec.created_at +
                                                                  'Z';

                                                        return new Date(
                                                            isoUtc
                                                        ).toLocaleString(
                                                            undefined,
                                                            {
                                                                year: 'numeric',
                                                                month: 'short',
                                                                day: 'numeric',
                                                                hour: '2-digit',
                                                                minute: '2-digit',
                                                                second: '2-digit',
                                                                hour12: false,
                                                            }
                                                        );
                                                    })()}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    noWrap
                                                >
                                                    by: {rec.user_email}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        {/*90%*/}
                                        {/* Right 5% icon slot */}
                                        <Box
                                            sx={{
                                                flex: '0 0 5%',
                                                display: 'flex',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            {rec.attachments_count > 0 && (
                                                <Tooltip
                                                    title={`Has ${rec.attachments_count ?? 0} attachment(s)`}
                                                >
                                                    <AttachFileIcon
                                                        fontSize="small"
                                                        color="primary"
                                                    />
                                                </Tooltip>
                                            )}
                                        </Box>
                                        {/*5%*/}
                                    </Box>
                                    <Box
                                        sx={{
                                            maxWidth: '100%',
                                        }}
                                    >
                                        {/* box for title*/}
                                        <Typography
                                            variant="subtitle1"
                                            gutterBottom
                                            noWrap
                                            sx={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                maxWidth: '100%', // ensure it doesn't grow beyond container
                                            }}
                                        >
                                            {rec.title}
                                        </Typography>
                                    </Box>
                                    {/* box for title */}
                                    <IconButton
                                        sx={{
                                            width: 36,
                                            height: 36,
                                        }}
                                        aria-label="delete"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            deleteDraft({ draftUUID: rec.id });
                                            // alert('bungus')
                                            setItems(
                                                items.filter(
                                                    (item) => item.id !== rec.id
                                                )
                                            );
                                        }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                            </CardContent>
                        </CardActionArea>
                    </Card>
                ))}
            </Box>

            {/* Pagination */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Pagination
                    count={Math.ceil(total / PAGE_SIZE)}
                    page={page}
                    onChange={(_, p) => setPage(p)}
                    disabled={loading}
                />
            </Box>
        </Container>
    );
};

export default HomePage;
